import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError, type AxiosProgressEvent } from "axios";
import type { IBusiness } from "@/business/business";
import { businessKey } from "@/business/query";
import { getEnrichedBusiness } from "@/business/utils";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import { onboardingKey } from "./onboarding-api";
import type {
  OnboardingDocument,
  OnboardingDocumentType,
  OnboardingDocumentResponse,
  OnboardingDocumentsResponse,
  UploadConflict,
  UploadConflictResolution,
} from "./types";

const onboardingDocumentsPath = (businessId: string) =>
  `/businesses/${businessId}/onboarding_documents`;

export const onboardingDocumentsKey = (businessId: string) =>
  ["onboarding-documents", businessId] as const;

export const useOnboardingDocuments = (businessId: string) => {
  const api = useApi();

  return useQuery({
    queryKey: onboardingDocumentsKey(businessId),
    queryFn: (): Promise<OnboardingDocument[]> =>
      api
        .get<OnboardingDocumentsResponse>(onboardingDocumentsPath(businessId))
        .then((response) => response.data.onboarding_documents),
    enabled: !!businessId,
  });
};

interface UploadOnboardingDocumentParams {
  file: File;
  documentType: OnboardingDocumentType;
  onProgress: (progress: number) => void;
  onConflict?: UploadConflictResolution;
}

export const createOnboardingDocumentFormData = (
  file: File,
  documentType: OnboardingDocumentType,
  onConflict?: UploadConflictResolution,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("document_type", documentType);
  if (onConflict) formData.append("on_conflict", onConflict);
  return formData;
};

// A 409 carries what the screen needs to ask the question, rather than an error to show.
// The api client turns every error into an ApiError, so read the body it kept. A raw
// AxiosError is still handled, for any caller that bypasses the interceptor.
export const extractUploadConflict = (
  error: unknown,
): UploadConflict | null => {
  let body: unknown = null;
  if (error instanceof ApiError && error.status === 409) {
    body = error.data;
  } else if (isAxiosError(error) && error.response?.status === 409) {
    body = error.response.data;
  }

  return (body as { conflict?: UploadConflict } | null)?.conflict ?? null;
};

export const getUploadProgress = ({
  loaded,
  total,
}: Pick<AxiosProgressEvent, "loaded" | "total">) => {
  if (!total) return 0;
  return Math.min(100, Math.round((loaded / total) * 100));
};

const submissionPath = (businessId: string) =>
  `/businesses/${businessId}/onboarding_document_submission`;

// Marking the set complete, and taking that back. Both answer with the business, so the
// shared business cache stays the single source of the flag.
export const useSetDocumentsReady = (businessId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ready: boolean): Promise<IBusiness> =>
      (ready
        ? api.post(submissionPath(businessId))
        : api.delete(submissionPath(businessId))
      ).then((response) => response.data),
    onSuccess: (business) => {
      void queryClient.invalidateQueries({
        queryKey: onboardingKey(businessId),
      });
      queryClient.setQueryData(
        businessKey(businessId),
        getEnrichedBusiness(business),
      );
    },
  });
};

export const useDeleteOnboardingDocument = (businessId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string): Promise<void> =>
      api
        .delete(`${onboardingDocumentsPath(businessId)}/${documentId}`)
        .then(() => undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: onboardingDocumentsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: businessKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: onboardingKey(businessId),
      });
    },
  });
};

export const useUploadOnboardingDocument = (businessId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      documentType,
      onProgress,
      onConflict,
    }: UploadOnboardingDocumentParams): Promise<OnboardingDocument> => {
      const formData = createOnboardingDocumentFormData(
        file,
        documentType,
        onConflict,
      );

      return api
        .post<OnboardingDocumentResponse>(
          onboardingDocumentsPath(businessId),
          formData,
          {
            onUploadProgress: (event) => onProgress(getUploadProgress(event)),
          },
        )
        .then((response) => response.data.onboarding_document);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: onboardingDocumentsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: businessKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: onboardingKey(businessId),
      });
    },
  });
};
